import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

/**
 * Copia o texto usando a Clipboard API quando disponível.
 * A API só existe em secure context (HTTPS ou localhost), então em ambientes
 * servidos por HTTP puro caímos no `execCommand('copy')`, que ainda funciona.
 */
export const copyToClipboard = async (text: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // segue para o fallback abaixo
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);

  try {
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    if (!document.execCommand('copy')) {
      throw new Error('execCommand("copy") failed');
    }
  } finally {
    document.body.removeChild(textarea);
  }
};

/**
 * Hook para compartilhar a página atual — ou um endereço específico dela.
 * Usa o compartilhamento nativo do dispositivo quando disponível;
 * senão copia o link para a área de transferência e avisa o usuário via toast.
 *
 * `url` existe para o que é compartilhável DENTRO da página: o episódio de um
 * card do bloco Rádio, por exemplo, cujo link não é o da página que o exibe.
 * Sem ele, cai no endereço atual — que é o comportamento de sempre.
 */
export const useSharePage = (
  title?: string,
  targetUrl?: string,
): (() => void) => {
  const intl = useIntl();

  return useCallback((): void => {
    const url = targetUrl || window.location.href;
    if (navigator.share) {
      navigator.share({ title, url }).catch(() => {});
      return;
    }
    copyToClipboard(url)
      .then(() => {
        toast.success(
          intl.formatMessage({
            id: 'Link copied to clipboard',
            defaultMessage: 'Link copiado para a área de transferência',
          }),
        );
      })
      .catch(() => {
        toast.error(
          intl.formatMessage({
            id: 'Error copying link to clipboard',
            defaultMessage: 'Erro ao copiar o link',
          }),
        );
      });
  }, [title, targetUrl, intl]);
};

export default useSharePage;
